(() => {
  'use strict';
  const clamp = value => Math.max(0, Math.min(1, value));
  const smooth = value => { const x = clamp(value); return x * x * (3 - 2 * x); };
  const softer = value => { const x = clamp(value); return x * x * x * (x * (x * 6 - 15) + 10); };
  const ease = value => Math.sin(clamp(value) * Math.PI / 2);
  const timing = { blank: 120, step: 20, textStart: 480, letterStep: 55, letterDuration: 180, complete: 1720, hold: 5000, exitStart: 6720, end: 8300, duration: 8600 };
  const effects = {
    contour: { delay: -450, duration: 5900, arrivalFraction: .90, tailFraction: .30, whiteFade: 1400 },
    sheen: { delay: 300, duration: 1500, angle: -180 / 7 },
    white: { delay: 1650, rise: 650, hold: 400, fall: 650 }
  };
  const poseAt = elapsed => {
    if (elapsed < timing.complete) return { phase: 'drawing', full: false, staticIso: false, frame: Math.max(0, Math.min(79, (elapsed - timing.blank) / timing.step)), letters: Array.from({ length: 11 }, (_, i) => ease((elapsed - timing.textStart - i * timing.letterStep) / timing.letterDuration)) };
    // The web intro runs once; the complete lockup then keeps showing the looping film.
    return { phase: elapsed < timing.duration ? 'hold' : 'playing', full: true, staticIso: true, frame: 79, letters: Array(11).fill(1) };
  };
  const whiteAt = elapsed => {
    const w = effects.white, t = elapsed - timing.complete - w.delay;
    if (t < 0 || t >= w.rise + w.hold + w.fall) return 0;
    if (t < w.rise) return smooth(t / w.rise);
    if (t < w.rise + w.hold) return 1;
    return 1 - smooth((t - w.rise - w.hold) / w.fall);
  };
  const contourAt = elapsed => {
    const q = effects.contour, raw = (elapsed - timing.complete - q.delay) / q.duration, p = clamp(raw);
    const w = effects.white, whiteStart = timing.complete + w.delay + w.rise;
    const visibility = elapsed < whiteStart ? 1 - softer((elapsed - whiteStart + q.whiteFade) / q.whiteFade) : softer((elapsed - whiteStart - w.hold) / q.whiteFade);
    return { active: raw > 0 && raw < 1 && visibility > 0, visibility, head: Math.min(1, p / q.arrivalFraction), tail: q.tailFraction * (1 - smooth((p - q.arrivalFraction) / (1 - q.arrivalFraction))), envelope: smooth(Math.min(1, p / .06, (1 - p) / (1 - q.arrivalFraction))) };
  };
  if (typeof document === 'undefined') {
    if (typeof module !== 'undefined') module.exports = { timing, effects, poseAt, whiteAt, contourAt };
    return;
  }
  const init = (scope = document) => {
    scope.querySelectorAll('[data-bc-footer]').forEach(root => {
      if (root.dataset.initialized) return;
      root.dataset.initialized = 'true';
      const brand = root.querySelector('[data-footer-brand]');
      if (!brand) return;
      const video = root.querySelector('[data-footer-video]');
      const reduced = matchMedia('(prefers-reduced-motion: reduce)');
      const mobile = matchMedia('(max-width: 749px)');
      const controller = new AbortController(), cleanup = [];
      const listen = (node, name, handler) => { if (!node) return; node.addEventListener(name, handler); cleanup.push(() => node.removeEventListener(name, handler)); };
      const layouts = {};
      ['desktop', 'mobile'].forEach(mode => {
        const art = root.querySelector('.bc-footer__art--' + mode);
        const fx = root.querySelector('.bc-footer__effects--' + mode);
        if (!art || !fx) return;
        const parts = [art, fx], find = selector => parts.flatMap(part => Array.from(part.querySelectorAll(selector)));
        const clip = fx.querySelector('[data-footer-contour-clip]');
        const contourPath = fx.querySelector('[data-footer-contour-path]');
        layouts[mode] = { art, fx, height: mode === 'mobile' ? 640 : 125, frames: find('[data-footer-draw-frame]'), glyphs: find('.bc-footer__glyph'), draw: find('.bc-footer__iso-draw'), vector: find('[data-footer-static-isotype]'), white: fx.querySelector('[data-footer-white]'), glow: fx.querySelector('[data-footer-contour-glow]'), tip: fx.querySelector('[data-footer-contour-tip]'), contourPath, contourLength: contourPath.getTotalLength(), clip, mask: clip.getAttribute('mask'), outline: fx.querySelector('[data-footer-contour-base]'), segments: Array.from(fx.querySelectorAll('[data-footer-contour-segment]')), sheen: fx.querySelector('[data-footer-sheen]'), band: fx.querySelector('[data-footer-sheen-band]'), lastFrame: -1, lastFull: null, lastLetters: [], lastStatic: null };
      });
      if (!layouts.desktop || !layouts.mobile) { root.classList.add('is-intro-complete'); return; }
      let near = false, visible = false, ready = false, loaded = false, failed = false, disposed = false;
      let elapsed = 0, lastNow = 0, raf = 0, promise, drawingImage, drawingUrl;
      const activeLayout = () => layouts[mobile.matches ? 'mobile' : 'desktop'];
      const alignGrid = () => {
        const bounds = root.getBoundingClientRect(), logo = brand.getBoundingClientRect();
        if (!logo.width) return;
        const scale = logo.width / 980, cell = 45 / scale;
        root.querySelectorAll('.bc-footer__art').forEach(art => {
          const pattern = art.querySelector('[data-footer-grid-pattern]'), line = art.querySelector('[data-footer-grid-line]'), gradient = art.querySelector('[data-footer-grid-gradient]');
          pattern.setAttribute('width', String(cell)); pattern.setAttribute('height', String(cell));
          pattern.setAttribute('x', String((bounds.left - logo.left) / scale)); pattern.setAttribute('y', String((bounds.bottom - logo.top) / scale));
          line.setAttribute('d', `M${cell} ${.5 / scale}H${.5 / scale}V${cell}`); line.setAttribute('stroke-width', String(1 / scale));
          gradient.setAttribute('y1', String((bounds.top - logo.top) / scale)); gradient.setAttribute('y2', String((bounds.bottom - logo.top) / scale));
        });
      };
      const render = (force = false) => {
        if (disposed || !ready || reduced.matches || failed) return;
        const view = activeLayout(), pose = poseAt(elapsed), frame = Math.floor(pose.frame);
        if (force || frame !== view.lastFrame) {
          view.frames.forEach(image => { image.setAttribute('x', String(-(frame % 10) * 600)); image.setAttribute('y', String(-Math.floor(frame / 10) * 272)); });
          view.lastFrame = frame;
        }
        if (force || pose.staticIso !== view.lastStatic) {
          view.vector.forEach(node => { node.style.opacity = pose.staticIso ? '1' : '0'; });
          view.draw.forEach(node => { node.style.opacity = pose.staticIso ? '0' : '1'; });
          view.lastStatic = pose.staticIso;
        }
        view.glyphs.forEach((node, index) => {
          const alpha = pose.letters[index % 11];
          if (!force && alpha === view.lastLetters[index]) return;
          node.style.opacity = String(alpha);
          // Keep the original inline geometry; use the approved tiny settling motion.
          const lift = (mobile.matches ? 9 : 1.6) * (1 - alpha);
          node.style.transform = `translateY(${lift}px)`;
          view.lastLetters[index] = alpha;
        });
        if (force || pose.full !== view.lastFull) {
          root.classList.toggle('is-logo-full', pose.full);
          if (pose.full) view.clip.removeAttribute('mask'); else view.clip.setAttribute('mask', view.mask);
          view.lastFull = pose.full;
        }
        const white = whiteAt(elapsed), glow = contourAt(elapsed);
        view.white.style.opacity = String(white);
        view.glow.style.opacity = glow.active ? String(glow.visibility) : '0';
        if (glow.active) {
          const point = view.contourPath.getPointAtLength(glow.head * view.contourLength);
          view.tip.setAttribute('transform', `translate(${point.x} ${point.y})`);
          view.tip.setAttribute('opacity', String(glow.envelope));
          view.outline.setAttribute('opacity', String(.12 * glow.envelope));
          const count = view.segments.length, segment = glow.tail / count;
          view.segments.forEach((node, index) => {
            const fraction = (index + 1) / count, start = glow.head - glow.tail + index * segment;
            node.setAttribute('stroke-dasharray', `${Math.max(.000001, segment)} 2`);
            node.setAttribute('stroke-dashoffset', String(-start));
            node.setAttribute('opacity', String(glow.envelope * fraction ** 1.45));
          });
        }
        const s = effects.sheen, p = (elapsed - timing.complete - s.delay) / s.duration;
        const sheen = p > 0 && p < 1;
        view.sheen.style.opacity = sheen ? String(Math.sin(Math.PI * p) ** .65) : '0';
        if (sheen) view.band.setAttribute('transform', `translate(${-980 * .45 + p * 980 * 1.9} ${view.height / 2}) rotate(${s.angle})`);
        brand.dataset.footerPhase = pose.phase;
        brand.dataset.footerEffect = white === 1 ? 'white' : glow.active ? 'contour' : 'none';
        brand.dataset.footerWhite = String(white);
        brand.dataset.footerTip = glow.active ? 'visible' : 'hidden';
        brand.dataset.footerGlowOpacity = glow.active ? String(glow.visibility) : '0';
        brand.dataset.footerTime = (elapsed / 1000).toFixed(2);
        brand.dataset.footerCycle = '0';
      };
      const active = () => ready && !failed && near && visible && !document.hidden && !reduced.matches && !disposed && elapsed < timing.duration;
      const tick = now => {
        raf = 0;
        if (!active()) { lastNow = 0; return; }
        if (lastNow) {
          elapsed = Math.min(timing.duration, elapsed + Math.min(80, now - lastNow));
        }
        lastNow = now; render(); raf = requestAnimationFrame(tick);
      };
      const failOpen = () => {
        if (disposed) return;
        failed = true; cancelAnimationFrame(raf); raf = 0;
        root.classList.remove('is-cycle-ready', 'is-symbol-drawing', 'is-logo-full'); root.classList.add('is-intro-complete');
        brand.dataset.footerPhase = 'static';
      };
      const loadDrawing = () => {
        if (promise) return promise;
        const source = brand.dataset.footerDrawingSrc;
        promise = fetch(source, { signal: controller.signal }).then(response => {
          if (!response.ok) throw new Error('Footer drawing unavailable');
          return response.blob();
        }).then(blob => new Promise((resolve, reject) => {
          if (disposed) { resolve(); return; }
          drawingUrl = URL.createObjectURL(blob); drawingImage = new Image();
          drawingImage.onload = () => {
            if (disposed) { resolve(); return; }
            Object.values(layouts).forEach(view => view.frames.forEach(image => image.setAttribute('href', drawingUrl)));
            ready = true; root.classList.add('is-cycle-ready', 'is-symbol-drawing'); root.dataset.footerEffects = 'v24';
            render(true); resolve(); update();
          };
          drawingImage.onerror = reject; drawingImage.src = drawingUrl;
        })).catch(error => { if (error.name !== 'AbortError') failOpen(); });
        return promise;
      };
      const update = () => {
        if (disposed) return;
        if (reduced.matches) {
          cancelAnimationFrame(raf); raf = 0; lastNow = 0;
          root.classList.remove('is-cycle-ready', 'is-logo-full'); root.classList.add('is-intro-complete');
          if (video) video.pause(); return;
        }
        if (ready && !failed) { root.classList.add('is-cycle-ready'); root.classList.remove('is-intro-complete'); }
        if (near && !failed) loadDrawing();
        const play = near && visible && !document.hidden;
        root.classList.toggle('is-motion-active', play);
        if (video) {
          if (near && !loaded) {
            video.querySelectorAll('source[data-src]').forEach(source => { source.src = source.dataset.src; });
            video.preload = 'auto'; video.load(); loaded = true;
          }
          if (play) { const playing = video.play(); if (playing) playing.catch(() => {}); } else video.pause();
        }
        if (active() && !raf) { lastNow = 0; raf = requestAnimationFrame(tick); }
        if (!active()) { cancelAnimationFrame(raf); raf = 0; lastNow = 0; }
      };
      if (video) { video.muted = true; video.defaultMuted = true; video.playsInline = true; }
      root.classList.add('is-prepared');
      const gridSize = new ResizeObserver(alignGrid); gridSize.observe(root); gridSize.observe(brand); alignGrid();
      const approach = new IntersectionObserver(([entry]) => { near = entry.isIntersecting; update(); }, { rootMargin: '600px' });
      const reveal = new IntersectionObserver(([entry]) => { visible = entry.isIntersecting && entry.intersectionRatio >= .15; root.classList.toggle('is-revealed', visible); update(); }, { threshold: [0, .15] });
      approach.observe(root); reveal.observe(brand);
      listen(video, 'playing', () => { root.classList.add('is-video-playing'); if (!near || !visible || document.hidden || reduced.matches) video.pause(); });
      listen(video, 'error', () => root.classList.remove('is-video-playing'));
      listen(document, 'visibilitychange', update);
      const reducedChange = () => { render(true); update(); };
      const mobileChange = () => { alignGrid(); render(true); };
      reduced.addEventListener('change', reducedChange); mobile.addEventListener('change', mobileChange);
      const language = root.querySelector('[data-footer-language]');
      listen(language, 'change', () => language.form.requestSubmit());
      listen(root.querySelector('[data-footer-top]'), 'click', event => { event.preventDefault(); window.scrollTo({ top: 0, behavior: reduced.matches ? 'instant' : 'smooth' }); document.getElementById('MainContent')?.focus({ preventScroll: true }); });
      const unload = event => {
        if (!event.target.contains(root)) return;
        disposed = true; cancelAnimationFrame(raf); approach.disconnect(); reveal.disconnect(); gridSize.disconnect(); controller.abort();
        if (video) video.pause(); if (drawingImage) drawingImage.onload = drawingImage.onerror = null;
        if (drawingUrl) URL.revokeObjectURL(drawingUrl);
        cleanup.forEach(remove => remove()); reduced.removeEventListener('change', reducedChange); mobile.removeEventListener('change', mobileChange);
        document.removeEventListener('shopify:section:unload', unload);
      };
      document.addEventListener('shopify:section:unload', unload);
      update();
    });
  };
  init();
  document.addEventListener('shopify:section:load', event => init(event.target));
})();
